/* eslint-disable max-len */
import { useDispatch, useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import AddNewReport from '../../../../member-detail/components/AddNewReport';
import NewSubstanceReport from '../../../../member-detail/components/SubstanceUse/substance-use-new';
import { profileActionCreators } from '../../../../../../redux/modules/profile';
import { getProfile } from '../../../../../../redux/modules/profile/selectors';
import { priorityListCard } from '../../../priority-list/PriorityList.constants';
import { stepTitles as diagnosesSteps } from '../../../../member-detail/components/Diagnoses/Diagnoses.constants';
import { stepTitles as lifeEventSteps } from '../../../../member-detail/components/LifeEvents/LifeEvent.constants';
import { stepTitles } from '../../../../member-detail/components/AddNewReport/AddNewReport.constants';
import { stepTitles as socialSteps } from '../../../../member-detail/components/SocialDeterminants/SocialDeterminants.constants';
import { DomainTypesEnum } from '../../../../../../constants/CommonConstants';
import { getAuth } from '../../../../../../redux/modules/auth/selectors';
import AddFactorDrawer, { DrawerType } from '../../../../../provider-v2/member-detail/add-factor-drawer';
import { FactorType } from '../../../../../provider-v2/member-detail/add-factor-drawer/FactorItem';
import AddNewReportMedications from '../../../../../provider-v2/member-detail/add-new-report/AddNewReportMedications';
import AddExternalRxDrawer from '../../../../../provider-v2/member-detail/medications/AddExternalRxDrawer';
import { useMemo } from 'react';
import { getAssignedPrioritiesV2 } from '../../../../../../services/priority/priority.service';
import {
  fetchNavItemStatus,
  initiateMdToolboxAutomation,
} from '../../../../../../services/datalab/datalab.service';
import { showSnackbar } from '../../../../../../redux/modules/snackbar';

const CreateNewReport = ({
  sectionType,
  isNoDrawer = false,
  memberId,
  onCloseCard,
  domainOptions = [],
  lookupData,
  onSelectInnerElementType,
  isLoadingOptions = false,
}) => {
  const dispatch = useDispatch();
  const { meta } = useSelector(getAuth);
  const { profile } = useSelector(getProfile);
  const [showMedicationSelection, setShowMedicationSelection] = useState(false);
  const [showExternalRx, setShowExternalRx] = useState(false);
  const [showMedicationDrawer, setShowMedicationDrawer] = useState(false);
  const [isSavingMedication, setIsSavingMedication] = useState(false);
  const [shouldStartAutomation, setShouldStartAutomation] = useState(false);
  const [isAutomationLoading, setIsAutomationLoading] = useState(false);
  const [automationSessionId, setAutomationSessionId] = useState(null);

  // Initialize medication selection drawer when component mounts for medications
  useEffect(() => {
    if (sectionType === priorityListCard[10].type) {
      setShowMedicationSelection(true);
    }
  }, []);

  const otherDomainElementId = useMemo(() => {
    for (const group of domainOptions) {
      const otherItem = group.items.find(item => item.label.toLowerCase() === 'other');
      if (otherItem) {
        return otherItem.value;
      }
    }
    return null;
  }, [domainOptions]);

  const onClose = () => {
    setShowMedicationSelection(false);
    setShowExternalRx(false);
    setShowMedicationDrawer(false);
    onCloseCard(false);
  };

  const onAutomationError = () => {
    setIsSavingMedication(false);
    setIsAutomationLoading(false);
    setShouldStartAutomation(false);
    setAutomationSessionId(null);
  };

  const startMdToolboxAutomation = async (patientId, associatedTagId, confidantProviderId) => {
    setIsAutomationLoading(true);
    try {
      const response = await initiateMdToolboxAutomation(
        {
          patientId,
          associatedTagId,
          providerId: confidantProviderId,
        },
        meta.userId,
      );

      if (!response.data?.success) {
        const errorMessage =
          response.data?.error?.message ||
          response.data?.message ||
          'Failed to submit the medication data. Please try again.';
        throw new Error(errorMessage);
      }

      const sessionId = response.data?.sessionId;

      if (!sessionId) {
        console.warn('No sessionId found in automation response');
      }
      setIsAutomationLoading(false);
      return { ...response, sessionId, isQueued: response.data?.isQueued };
    } catch (error) {
      setIsAutomationLoading(false);
      const errorMessage =
        error.data?.error?.message ||
        error.data?.message ||
        'Failed to submit the medication data. Please try again.';

      throw new Error(errorMessage);
    }
  };

  const onSubmitNewReport = payload => {
    const data = {
      patientId: memberId,
      tagItems: payload.map(item => ({
        assignedBy: profile.fullName || meta.nickName,
        domainElementId: item.item.value,
        selectedIct10code: item.icd10,
        metaData: {
          interferenceInLife: item.isInterference,
          notes: item.notes,
          selectedImportance: item.status,
        },
      })),
    };
    dispatch(
      profileActionCreators.createNewAssociatedTag({
        data,
        callback: errorMsg => {
          if (!errorMsg) {
            onCloseCard(true);
          }
        },
      }),
    );
  };

  const onSubmitNewMedicationReport = (payload, handleQueued) => {
    setIsSavingMedication(true);

    const data = {
      patientId: memberId,
      tagItems: payload.medications.map(item => ({
        assignedBy: profile.fullName || meta.nickName,
        domainElementId: item.item.value,
        metaData: {
          notes: item?.additionalNotes,
          selectedImportance: item?.status,
          rxDrugInfo: {
            sig: item?.sig,
            externalMedicationId: item?.externalMedicationId,
            externalMedicationName: item?.externalMedicationName,
            refills: item?.refills,
            dispenseAmount: item?.dispenseAmount ? Number(item.dispenseAmount) : 0,
            associatedDiagnoses: item?.associatedDiagnoses,
            allowSubstitutions: item?.allowSubstitutions,
            pharmacyNotes: item?.pharmacyNotes,
            additionalNotes: item?.additionalNotes,
            quantity: item.quantity ? Number(item.quantity) : 0,
            dose: item.dose ?? '',
            frequency: item?.frequency?.value ?? item?.frequency,
            duration: item?.duration ? Number(item.duration) : 0,
            confidantProviderId: item?.confidantProvider,
            confidantProviderName: item?.providerName,
            prescribingProvider: item?.provider === 'Confidant Provider' ? 'Internal' : 'External',
            providerName: item?.providerName,
            symptomsBeingTreated: item?.symptoms,
            pharmacyId: payload.pharmacy?.id,
            pharmacyName: payload.pharmacy?.name,
            pharmacyCode: payload.pharmacy?.externalId,
            pharmacyZipCode: payload.pharmacy?.zipCode,
          },
        },
      })),
    };

    dispatch(
      profileActionCreators.createNewAssociatedTag({
        data,
        callback: async (errorMsg, response) => {
          if (errorMsg) {
            setIsSavingMedication(false);
            dispatch(
              showSnackbar({
                snackType: 'error',
                snackMessage: errorMsg || 'Failed to create medication. Please try again.',
              }),
            );
            onCloseCard(true);
            return;
          }

          try {
            // Fetch the records again to get the newly created association
            const { data: updatedRecords } = await getAssignedPrioritiesV2({
              patientId: memberId,
              dataDomainType: 'medications',
              pageNumber: 0,
              pageSize: 20,
              importanceLevels: '',
              searchQuery: '',
            });

            // Find the newly created association by matching domainElementId
            const targetDomainElementId = payload.medications[0].item.value;
            const newlyCreatedAssociation = updatedRecords.records.find(
              record => record.domainElementId === targetDomainElementId,
            );

            if (!newlyCreatedAssociation) {
              throw new Error('Could not find the newly created medication association');
            }

            setIsSavingMedication(false);

            // Use the association ID from the fetched record
            const associatedTagId = newlyCreatedAssociation.id;
            const confidantProviderId = payload.medications[0]?.confidantProvider;

            if (confidantProviderId) {
              const status = await fetchNavItemStatus({ providerId: confidantProviderId });
              const statusData = status?.data?.data;
              if (!statusData || !statusData['MDToolboxAccess']) {
                // Silently skip automation because of missing mdtoolbox credentials
                onCloseCard(true);
                return;
              }

              const { sessionId, isQueued } = await startMdToolboxAutomation(
                memberId,
                associatedTagId,
                confidantProviderId,
              );

              if (sessionId) {
                // Store sessionId and trigger automation in child component
                setAutomationSessionId(sessionId);
                setShouldStartAutomation(true);
                if (isQueued && handleQueued) {
                  handleQueued();
                }
              } else {
                console.warn('No Session id received, cannot submit medication');
                onCloseCard(true);
              }
            } else {
              // No confidantProviderId, just close the drawer normally
              onCloseCard(true);
            }
          } catch (error) {
            dispatch(
              showSnackbar({
                snackType: 'error',
                snackMessage: error.message || 'Failed to submit the medication data. Please try again.',
              }),
            );
            // Reset automation states but keep drawer open
            onCloseCard(true);
          }
        },
      }),
    );
  };

  const onSubmitNewSubstanceUseReport = payload => {
    const data = {
      patientId: memberId,
      tagItems: payload.map(item => ({
        assignedBy: profile.fullName || meta.nickName,
        domainElementId: item.item.value,
        metaData: {
          interferenceInLife: item.isInterference,
          notes: item.notes,
          selectedImportance: item.status,
          substanceUse: {
            currentFrequencyOfUse: item.tutorial.frequency,
            howLongUsingThisLevel: item.tutorial.duration,
            lastUse: item.tutorial.lastUse,
            methodOfUse: item.tutorial.method,
            unitOfUse: item.tutorial.dose.unit,
            dose: item.tutorial.dose.amount,
            refillable: item.tutorial.refillable,
            refillTimes: item.tutorial.refillTimes.amount,
          },
        },
      })),
    };
    dispatch(
      profileActionCreators.createNewAssociatedTag({
        data,
        callback: errorMsg => {
          if (!errorMsg) {
            onCloseCard(true);
          }
        },
      }),
    );
  };

  const getCard = () => {
    switch (sectionType) {
      case priorityListCard[4].type:
      case priorityListCard[9].type: {
        const isSocial = sectionType === priorityListCard[4].type;
        return (
          <AddNewReport
            isOpen
            isNoDrawer={isNoDrawer}
            onClose={onClose}
            type={isSocial ? DomainTypesEnum.SOCIALS_DETERMINANTS : DomainTypesEnum.DIAGNOSES}
            title={isSocial ? 'Report new social determinant' : 'Report new diagnoses'}
            stepTitles={isSocial ? socialSteps : diagnosesSteps}
            showInterference={false}
            searchLabel={`Select one or more ${isSocial ? 'social determinants' : 'diagnoses'}`}
            options={domainOptions}
            onSubmit={onSubmitNewReport}
            isLoadingOptions={isLoadingOptions}
          />
        );
      }
      case priorityListCard[5].type:
        return (
          <AddNewReport
            isOpen
            isNoDrawer={isNoDrawer}
            type={DomainTypesEnum.LIFE_EVENTS}
            onClose={onClose}
            title="Report new life events"
            stepTitles={lifeEventSteps}
            isLifeEvent={false}
            showInterference={false}
            searchLabel="Select one or more life events"
            options={domainOptions}
            onSubmit={onSubmitNewReport}
            isLoadingOptions={isLoadingOptions}
          />
        );
      case priorityListCard[6].type: {
        const isSocial = sectionType === priorityListCard[6].type;
        return (
          <AddNewReport
            isOpen
            isNoDrawer={isNoDrawer}
            onClose={onClose}
            type={isSocial ? DomainTypesEnum.SOCIALS_DETERMINANTS : DomainTypesEnum.DIAGNOSES}
            title={isSocial ? 'Report new social determinant' : 'Report new daignoses'}
            stepTitles={isSocial ? socialSteps : diagnosesSteps}
            showInterference={false}
            searchLabel={`Select one or more ${isSocial ? 'social determinants' : 'diagnoses'}`}
            options={domainOptions}
            onSubmit={onSubmitNewReport}
            isLoadingOptions={isLoadingOptions}
          />
        );
      }
      case priorityListCard[7].type:
        return (
          <AddNewReport
            isOpen
            isNoDrawer={isNoDrawer}
            type={DomainTypesEnum.LIFE_EVENTS}
            onClose={onClose}
            title="Report new life events"
            stepTitles={lifeEventSteps}
            isLifeEvent={false}
            showInterference={false}
            searchLabel="Select one or more life events"
            options={domainOptions}
            onSubmit={onSubmitNewReport}
            isLoadingOptions={isLoadingOptions}
          />
        );
      case priorityListCard[8].type: {
        return (
          <AddNewReport
            title="Report new symptoms"
            stepTitles={stepTitles}
            showInterference
            searchLabel="Select one or more symptoms"
            options={domainOptions}
            onClose={onClose}
            isNoDrawer={isNoDrawer}
            onSubmit={onSubmitNewReport}
            type={DomainTypesEnum.SYMPTOMS}
            isOpen
            isLoadingOptions={isLoadingOptions}
          />
        );
      }
      case priorityListCard[10].type:
        // Show medication selection drawer first
        if (showMedicationSelection) {
          return (
            <AddFactorDrawer
              open={showMedicationSelection}
              onClose={() => {
                setShowMedicationSelection(false);
                onClose();
              }}
              title="Add Medication"
              variant={DrawerType.MEDICATION}
              onChangeSelect={selected => {
                setShowMedicationSelection(false);
                if (selected === FactorType.SEND_RX) {
                  setShowMedicationDrawer(true);
                } else if (selected === FactorType.EXTERNAL_RX) {
                  setShowExternalRx(true);
                }
              }}
            />
          );
        }
        // Show external RX drawer
        if (showExternalRx) {
          return (
            <AddExternalRxDrawer
              open={showExternalRx}
              onClose={shouldRefresh => {
                setShowExternalRx(false);
                // Close the entire CreateNewReport component
                onCloseCard(shouldRefresh);
              }}
              memberId={memberId}
              otherDomainElementId={otherDomainElementId}
            />
          );
        }
        // Show normal medication drawer
        if (showMedicationDrawer) {
          return (
            <AddNewReportMedications
              isOpen={showMedicationDrawer}
              onClose={forceRefresh => {
                setShowMedicationDrawer(false);
                if (forceRefresh) {
                  onCloseCard(true);
                } else {
                  onClose();
                }
              }}
              options={domainOptions}
              lookupData={lookupData}
              onSubmit={onSubmitNewMedicationReport}
              optionsLoading={isLoadingOptions}
              memberId={memberId}
              shouldStartAutomation={shouldStartAutomation}
              setShouldStartAutomation={setShouldStartAutomation}
              isAutomationLoading={isAutomationLoading}
              automationSessionId={automationSessionId}
              onAutomationSuccess={() => {
                setAutomationSessionId(null);
                setShouldStartAutomation(false);
                setIsAutomationLoading(false);
                setIsSavingMedication(false);
                onCloseCard(true);
              }}
              isSaving={isSavingMedication}
              onAutomationError={onAutomationError}
            />
          );
        }
        // Return null while waiting for useEffect to set initial state
        return null;
      case priorityListCard[11].type:
        return (
          <AddNewReport
            isOpen
            onClose={onClose}
            title="Report new side effect"
            stepTitles={stepTitles}
            showInterference={false}
            searchLabel="Select one or more side effects"
            options={domainOptions}
            onSubmit={onSubmitNewReport}
            isLoadingOptions={isLoadingOptions}
          />
        );
      case priorityListCard[12].type:
        return (
          <NewSubstanceReport
            isOpen
            isNoDrawer={isNoDrawer}
            onClose={onClose}
            options={domainOptions}
            lookupData={lookupData}
            onSubmit={onSubmitNewSubstanceUseReport}
            isLoadingOptions={isLoadingOptions}
          />
        );
      case 'symptomatic-factor': {
        return (
          <AddFactorDrawer
            open
            onClose={onClose}
            title="Add symptomatic factor"
            selectedType={sectionType}
            variant={DrawerType.PRIMARY}
            onChangeSelect={onSelectInnerElementType}
          />
        );
      }
      case 'environmental-factor': {
        return (
          <AddFactorDrawer
            open
            onClose={onClose}
            title="Add environmental factor"
            variant={DrawerType.SECONDARY}
            selectedType={sectionType}
            onChangeSelect={onSelectInnerElementType}
          />
        );
      }
      default:
        return null;
    }
  };

  return <>{getCard()}</>;
};

export { CreateNewReport };
